import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { CircularProgress, IconButton } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";

import { getFilesFileIDDownloadUrl } from "Infrastructure/Api/Api";
import { processResponse } from "Infrastructure/Api/ApiUtils";
import { useAudioPlayerStore } from "Infrastructure/Stores/audioPlayerStore";

interface MediaFilePlayerProps {
  fileID: string;
}

export const MediaFilePlayer = ({
  fileID,
}: MediaFilePlayerProps): React.ReactNode => {
  const instanceId = useId();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const pendingPlayRef = useRef(false);

  const playingInstanceId = useAudioPlayerStore((s) => s.playingInstanceId);
  const setPlayingInstanceId = useAudioPlayerStore(
    (s) => s.setPlayingInstanceId,
  );

  const { data: urlData, isFetching } = useQuery({
    queryKey: ["FILE_DOWNLOAD_URL", fileID],
    queryFn: processResponse(() => getFilesFileIDDownloadUrl(fileID)),
    enabled,
    staleTime: 60 * 1000,
  });

  // Auto-play once URL arrives after user clicked play
  useEffect(() => {
    if (urlData && pendingPlayRef.current) {
      pendingPlayRef.current = false;
      const audio = audioRef.current;
      if (audio) {
        audio.play();
        setIsPlaying(true);
        setPlayingInstanceId(instanceId);
      }
    }
  }, [urlData, instanceId, setPlayingInstanceId]);

  // Stop when another player starts playing
  useEffect(() => {
    if (isPlaying && playingInstanceId !== instanceId) {
      audioRef.current?.pause();
      setIsPlaying(false);
    }
  }, [playingInstanceId, instanceId, isPlaying]);

  const handleTogglePlay = () => {
    if (!enabled) {
      pendingPlayRef.current = true;
      setEnabled(true);
      return;
    }

    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      if (playingInstanceId === instanceId) setPlayingInstanceId(null);
    } else {
      audio.play();
      setIsPlaying(true);
      setPlayingInstanceId(instanceId);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    if (playingInstanceId === instanceId) setPlayingInstanceId(null);
  };

  return (
    <>
      {isFetching ? (
        <CircularProgress size={20} />
      ) : (
        <IconButton size="small" onClick={handleTogglePlay} color="inherit">
          {isPlaying ? (
            <PauseIcon sx={{ fontSize: 20 }} />
          ) : (
            <PlayArrowIcon sx={{ fontSize: 20 }} />
          )}
        </IconButton>
      )}
      {urlData && (
        // biome-ignore lint/a11y/useMediaCaption: audio preview player without transcript
        <audio ref={audioRef} src={urlData.downloadUrl} onEnded={handleEnded} />
      )}
    </>
  );
};
